import React, { useState, useEffect, useCallback } from 'react';
import { oneDayAPI, calendarAPI, holidayAPI } from '../../services/index';
import { LessonApplicationRequest, OneDayApplicationRequest, Holiday } from '../../types/api';
import SimpleCalendar from '../../components/SimpleCalendar';
import { useLanguage } from '../../contexts/LanguageContext';
import { useScrollToTop, usePhoneNumber } from '../../hooks';
import toast from 'react-hot-toast';
import { replaceWeekdaysInString, validateBirthDate, isUnder14 } from '../../utils';
import { TIME_SLOTS, WEEKEND_TIME_SLOTS } from '../../config/constants';
import { isBefore, startOfDay } from 'date-fns';

const OneDayClass: React.FC = () => {
  const { t } = useLanguage();

  // 페이지 마운트 시 상단으로 스크롤
  useScrollToTop();

  const [formData, setFormData] = useState<LessonApplicationRequest>({
    name: '',
    birthDate: '',
    gender: 'male',
    phoneNumber: '',
    email: '',
    preferredTime: '',
    message: '',
  });

  // 전화번호 훅 사용
  const { phoneNumber, handlePhoneChange, getCleanPhoneNumber } = usePhoneNumber();

  const [selectedDateTimes, setSelectedDateTimes] = useState<{ [date: string]: string[] }>({});
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [busyTimes, setBusyTimes] = useState<{ [date: string]: Array<{ startTime: string; endTime: string; studentName?: string; type: string }> }>({});
  const [selectedTeacher] = useState<string>('1'); // 현재 선생님 한 명뿐
  const [birthDateError, setBirthDateError] = useState<string>('');
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isGuardianAgreed, setIsGuardianAgreed] = useState<boolean>(false);

  // 만 14세 미만이면 법정대리인(보호자) 동의 필요
  const requiresGuardianConsent = isUnder14(formData.birthDate);

  // 학원 휴일 조회
  const fetchHolidays = useCallback(async (date: Date) => {
    try {
      const response = await holidayAPI.getHolidays({
        year: date.getFullYear(),
        month: date.getMonth() + 1
      });
      if (response.data?.success && response.data?.data) {
        setHolidays(response.data.data);
      }
    } catch {
      // 휴일 조회 실패
    }
  }, []);

  // 월 변경 시 휴일 다시 조회
  useEffect(() => {
    fetchHolidays(currentMonth);
  }, [currentMonth, fetchHolidays]);

  // 선택된 날짜가 학원 휴일인지 확인
  const isDateAcademyHoliday = useCallback((dateStr: string): Holiday | null => {
    return holidays.find(h => dateStr >= h.startDate && dateStr <= h.endDate) || null;
  }, [holidays]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    // 전화번호는 별도 훅에서 처리
    if (name === 'phoneNumber') return;

    // 생년월일 유효성 검증
    if (name === 'birthDate') {
      const validation = validateBirthDate(value);
      setBirthDateError(validation.isValid ? '' : validation.errorMessage || '');
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 선생님 바쁜 시간대 조회 (통합 캘린더 API 사용)
  const loadTeacherBusyTimes = async (dateString: string) => {
    if (!selectedTeacher) {
      return;
    }

    try {
      // 통합 캘린더 API로 해당 날짜의 모든 일정 조회
      const response = await calendarAPI.getUnifiedCalendar({
        startDate: dateString,
        endDate: dateString,
        teacherId: parseInt(selectedTeacher),
        includeRegular: true,
        includeModifications: true // 원데이클래스는 변경사항 적용된 일정 확인
      });

      if (response.data && response.data.success) {
        const eventsByDate = response.data.data.events || [];

        // events 배열의 각 날짜별 데이터에서 schedules를 추출
        const allSchedules: any[] = [];
        eventsByDate.forEach((dayEvent: any) => {
          if (dayEvent.schedules && Array.isArray(dayEvent.schedules)) {
            dayEvent.schedules.forEach((schedule: any) => {
              allSchedules.push({
                ...schedule,
                date: dayEvent.date
              });
            });
          }
        });

        // 바쁜 시간대 형식으로 변환
        const allBusyTimes = allSchedules.map((event: any) => ({
          startTime: event.startTime.substring(0, 5), // HH:MM:SS -> HH:MM
          endTime: event.endTime.substring(0, 5),
          studentName: event.studentName || event.title || t('busy_time'),
          type: event.scheduleType
        }));

        setBusyTimes(prev => ({
          ...prev,
          [dateString]: allBusyTimes
        }));
      }
    } catch {
      // 선생님 일정 조회 오류
    }
  };

  const handleDateClick = async (date: Date) => {
    // 오늘보다 이전 날짜는 클릭할 수 없음
    const today = startOfDay(new Date());
    if (isBefore(date, today)) {
      toast.error(t('cannot_select_past_date'));
      return;
    }

    // 시간대 문제 해결: 로컬 날짜로 변환
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;

    const newSelectedDate = selectedDate === dateString ? null : dateString;
    setSelectedDate(newSelectedDate);

    // 선택된 날짜의 선생님 일정 가져오기 (휴일이 아닌 경우에만)
    if (newSelectedDate && !isDateAcademyHoliday(dateString)) {
      await loadTeacherBusyTimes(newSelectedDate);
    }
  };

  // 시간이 바쁜 시간대인지 확인
  const isTimeSlotBusy = (time: string, date: string) => {
    const dateBusyTimes = busyTimes[date] || [];
    const [hour, minute] = time.split(':').map(Number);
    const timeInMinutes = hour * 60 + minute;

    return dateBusyTimes.some(busyTime => {
      const [startHour, startMin] = busyTime.startTime.split(':').map(Number);
      const [endHour, endMin] = busyTime.endTime.split(':').map(Number);
      const startInMinutes = startHour * 60 + (startMin || 0);
      const endInMinutes = endHour * 60 + (endMin || 0);
      return timeInMinutes >= startInMinutes && timeInMinutes < endInMinutes;
    });
  };

  // 다음 30분이 비어있어서 합칠 수 있는지 확인
  const canMergeWithNext = (time: string, date: string) => {
    const [hour, minute] = time.split(':').map(Number);
    const nextMinutes = hour * 60 + minute + 30;
    const nextTime = `${String(Math.floor(nextMinutes / 60)).padStart(2, '0')}:${String(nextMinutes % 60).padStart(2, '0')}`;
    // 다음 30분이 바쁘지 않으면 1시간 수업 가능
    return !isTimeSlotBusy(nextTime, date);
  };

  // 마지막 슬롯인지 확인 (주말/평일에 따라 다름)
  const isLastSlot = (time: string, isWeekend: boolean): boolean => {
    const [hour, minute] = time.split(':').map(Number);
    const timeMinutes = hour * 60 + minute;
    // 주말: 17:00까지만 선택 가능 (17:30, 18:00 불가)
    // 평일: 21:00까지만 선택 가능 (21:30, 22:00 불가)
    const maxMinutes = isWeekend ? 17 * 60 : 21 * 60;
    return timeMinutes > maxMinutes;
  };

  const handleTimeSelection = (time: string) => {
    if (!selectedDate) return;

    // 바쁜 시간대는 선택할 수 없음
    if (isTimeSlotBusy(time, selectedDate)) {
      return;
    }

    setSelectedDateTimes(prev => {
      const currentTimes = prev[selectedDate] || [];
      const isSelected = currentTimes.includes(time);

      if (isSelected) {
        const newTimes = currentTimes.filter(t => t !== time);
        if (newTimes.length === 0) {
          const newDateTimes = { ...prev };
          delete newDateTimes[selectedDate];
          return newDateTimes;
        }
        return {
          ...prev,
          [selectedDate]: newTimes
        };
      } else {
        return {
          ...prev,
          [selectedDate]: [...currentTimes, time].sort()
        };
      }
    });
  };

  const formatSelectedDateTime = () => {
    const dateTimeEntries = Object.entries(selectedDateTimes).filter(([_, times]) => times.length > 0);
    if (dateTimeEntries.length === 0) return '';

    return dateTimeEntries.map(([date, times]) => {
      const dateObj = new Date(date + 'T00:00:00');
      // 영어 요일을 사용하여 표시
      const englishWeekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const dateString = `${dateObj.getMonth() + 1}/${dateObj.getDate()}(${englishWeekdays[dateObj.getDay()]})`;
      return `${dateString}: ${times.join(', ')}`;
    }).join(' | ');
  };

  // 선택된 날짜들을 Calendar 컴포넌트가 이해할 수 있는 이벤트 형태로 변환
  const calendarEvents = Object.keys(selectedDateTimes).map(date => ({
    id: date,
    title: t('selected_date_label'),
    type: 'lesson' as const,
    start: new Date(date + 'T00:00:00'),
    end: new Date(date + 'T00:00:00'),
    color: '#1e3a8a', // 남색 테마에 맞춤
    data: {} as any // 실제 데이터는 없지만 타입을 맞추기 위해 빈 객체
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // 생년월일 유효성 검증
    const birthDateValidation = validateBirthDate(formData.birthDate);
    if (!birthDateValidation.isValid) {
      setBirthDateError(birthDateValidation.errorMessage || '');
      setIsSubmitting(false);
      toast.error(birthDateValidation.errorMessage || t('check_birth_date'));
      return;
    }

    // 만 14세 미만은 법정대리인(보호자) 동의 필수
    if (requiresGuardianConsent && !isGuardianAgreed) {
      setIsSubmitting(false);
      toast.error(t('guardian_consent_required'));
      return;
    }

    // 선택된 날짜들이 휴일인지 확인 (안전장치)
    const selectedDates = Object.keys(selectedDateTimes);
    for (const dateStr of selectedDates) {
      try {
        const holidayResponse = await holidayAPI.checkHoliday(dateStr);
        if (holidayResponse.data?.success && holidayResponse.data.data?.isHoliday) {
          const holidayTitle = holidayResponse.data.data.holiday?.title || t('academy_holiday');
          toast.error(t('holiday_date_unavailable', { date: dateStr, title: holidayTitle }));
          setIsSubmitting(false);
          return;
        }
      } catch {
        // 휴일 확인 실패 시 계속 진행
      }
    }

    try {
      const submissionData: OneDayApplicationRequest = {
        name: formData.name,
        birthDate: formData.birthDate,
        phoneNumber: getCleanPhoneNumber(),
        email: formData.email,
        preferredTime: replaceWeekdaysInString(formatSelectedDateTime()),
        message: formData.message
      };

      const response = await oneDayAPI.apply(submissionData);
      if (response.success) {
        setFormData({
          name: '',
          birthDate: '',
          gender: 'male',
          phoneNumber: '',
          email: '',
          preferredTime: '',
          message: '',
        });
        // 전화번호 훅 초기화
        handlePhoneChange('');
        setSelectedDateTimes({});
        setSelectedDate(null);
        setBirthDateError('');

        // 성공 메시지 토스트 표시
        toast.success(t('oneday_apply_success'));

        // 맨 위로 스크롤
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        toast.error(t('oneday_apply_error'));
      }
    } catch {
      toast.error(t('oneday_apply_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="oneday-class-page">
      <div className="page-content">
        <div className="container">

          <form onSubmit={handleSubmit} className="application-form odc-layout">
            {/* 왼쪽: 클래스 소개 + 신청자 정보 */}
            <div className="odc-left">
              <div className="odc-info-card">
                <div className="odc-info-title-row">
                  <h2 className="odc-info-title">{t('oneday_class')}</h2>
                  <span className="odc-badge odc-badge-time">{t('odc_info_badge_time')}</span>
                  <span className="odc-badge odc-badge-free">{t('odc_info_badge_free')}</span>
                </div>
                <p className="odc-info-desc">{t('fee_description')}</p>
                <div className="odc-info-grid">
                  <div className="odc-info-label">{t('lesson_structure_info')}</div>
                  <div className="odc-info-value">
                    {t('lesson_structure_step_1')} → {t('lesson_structure_step_2')} → {t('lesson_structure_step_3')} → {t('lesson_structure_step_4')}
                  </div>
                  <div className="odc-info-label">{t('lesson_fee_info')}</div>
                  <div className="odc-info-value odc-info-free">
                    {t('fee_amount')} <span className="odc-info-sub">· {t('fee_notice')}</span>
                  </div>
                  <div className="odc-info-label">{t('odc_people_label')}</div>
                  <div className="odc-info-value">{t('individual_lesson')}</div>
                </div>
              </div>

              <div className="odc-form-panel">
                <h3 className="odc-panel-title">{t('basic_info')}</h3>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">{t('name')}</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="birthDate" className="form-label">{t('birth_date')}</label>
                    <input
                      type="text"
                      id="birthDate"
                      name="birthDate"
                      value={formData.birthDate}
                      onChange={handleInputChange}
                      className={`form-input ${birthDateError ? 'error' : ''}`}
                      placeholder={t('birth_date_format')}
                      maxLength={8}
                      pattern="[0-9]{8}"
                      required
                    />
                    {birthDateError ? (
                      <small className="form-error">{birthDateError}</small>
                    ) : (
                      <small className="form-help">{t('birth_date_format_help')}</small>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phoneNumber" className="form-label">{t('phone')}</label>
                    <input
                      type="tel"
                      id="phoneNumber"
                      value={phoneNumber}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      className="form-input"
                      placeholder="010-1234-5678"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="form-label">{t('email')}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="example@email.com"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label unrequired">{t('additional_inquiry')}</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="form-textarea"
                    rows={3}
                    placeholder={t('message_placeholder')}
                  />
                </div>

              </div>
            </div>

            {/* 오른쪽: 희망 날짜 및 시간 */}
            <div className="odc-right">
              <div className="odc-schedule-panel">
                <div className="odc-schedule-header">
                  <h3 className="odc-panel-title">{t('odc_schedule_title')}</h3>
                </div>
                <div className="odc-schedule-body">
                  <p className="form-help-text">
                    {t('date_time_description')}
                  </p>

                  <div className="calendar-container">
                    <SimpleCalendar
                      events={calendarEvents}
                      holidays={holidays}
                      onDateClick={handleDateClick}
                      onMonthChange={(date) => setCurrentMonth(date)}
                    />
                  </div>

                  {selectedDate && (() => {
                    const academyHoliday = isDateAcademyHoliday(selectedDate);

                    // 학원 휴일인 경우 시간 선택 대신 휴일 안내 표시
                    if (academyHoliday) {
                      return (
                        <div className="time-selection-for-date holiday-notice">                          <h4>{(() => {
                            const dateObj = new Date(selectedDate + 'T00:00:00');
                            const englishWeekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                            return `${dateObj.getMonth() + 1}/${dateObj.getDate()}(${englishWeekdays[dateObj.getDay()]})`;
                          })()}</h4>
                          <p className="holiday-message">
                            {t('holiday_lesson_unavailable', { title: academyHoliday.title })}
                          </p>
                        </div>
                      );
                    }

                    const dateObj = new Date(selectedDate + 'T00:00:00');
                    const dayOfWeek = dateObj.getDay();
                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                    const englishWeekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

                    const timeSlots: readonly string[] = isWeekend ? WEEKEND_TIME_SLOTS : TIME_SLOTS;

                    return (
                      <div className="time-selection-for-date">
                        <h4>{`${dateObj.getMonth() + 1}/${dateObj.getDate()}(${englishWeekdays[dayOfWeek]}) ${t('time_selection')}`}</h4>
                        <div className="time-slots">
                          {timeSlots
                            .filter((time: string) => !isLastSlot(time, isWeekend))
                            .map((time: string) => {
                              const isBusy = isTimeSlotBusy(time, selectedDate);
                              const canMerge = canMergeWithNext(time, selectedDate);
                              const isDisabled = isBusy || !canMerge;
                              const isSelected = selectedDateTimes[selectedDate]?.includes(time);

                              // 종료 시간 계산 (1시간 후)
                              const [hour, min] = time.split(':').map(Number);
                              const endTime = `${String(hour + 1).padStart(2, '0')}:${String(min).padStart(2, '0')}`;

                              return (
                                <button
                                  key={`${selectedDate}-${time}`}
                                  type="button"
                                  className={`time-slot ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                                  onClick={() => !isDisabled && handleTimeSelection(time)}
                                  disabled={isDisabled}
                                  title={isBusy ? t('time_already_reserved') : !canMerge ? t('one_hour_lesson_unavailable') : t('one_hour_lesson_slot', { start: time, end: endTime })}
                                >
                                  {time}
                                </button>
                              );
                            })}
                        </div>
                      </div>
                    );
                  })()}

                  {Object.keys(selectedDateTimes).length > 0 && (
                    <div className="selected-dates-preview">
                      <strong>{t('selected_date_time')}</strong>
                      <p>{formatSelectedDateTime()}</p>
                    </div>
                  )}

                  <div className="odc-schedule-note">{t('odc_schedule_note')}</div>
                </div>
              </div>
            </div>

            {/* 제출 영역: 날짜·시간 선택 이후에 노출 */}
            <div className="odc-actions">
              {/* 만 14세 미만: 법정대리인(보호자) 동의 */}
              {requiresGuardianConsent && (
                <div className="agreement-section">
                  <p className="guardian-consent-notice">{t('guardian_consent_notice')}</p>
                  <label className="checkbox-label agreement-checkbox">
                    <input
                      type="checkbox"
                      checked={isGuardianAgreed}
                      onChange={(e) => setIsGuardianAgreed(e.target.checked)}
                    />
                    <span className="checkbox-text">{t('guardian_consent_label')}</span>
                  </label>
                </div>
              )}

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary btn-md"
                  disabled={isSubmitting || (requiresGuardianConsent && !isGuardianAgreed)}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span>
                      {t('applying')}
                    </>
                  ) : (
                    t('apply_oneday')
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OneDayClass; 